import { useQuery } from "@tanstack/react-query"

export function walletBalanceQueryKey(address: string) {
  return ["walletBalance", address] as const
}

export type UseWalletBalanceOptions = {
  /**
   * Subscribe to Toncenter only when true. Use while the user can see on-chain TON
   * (Profile bar / open deposit or withdraw) so the hook does not poll from hidden UI.
   */
  enabled?: boolean
}

export type WalletBalance = {
  /** Integer nanotons from Toncenter (`result`). */
  nano: number
  /** Derived for display only; prefer `nano` for limits and max-deposit math. */
  ton: number
}

/**
 * Fetches the on-chain TON balance for a connected wallet address.
 * Uses the public toncenter.com API (no API key required, 1 req/s rate limit).
 * Returns exact nanotons + derived TON, or null on error/no address.
 */
export function useWalletBalance(
  address: string | null,
  options: UseWalletBalanceOptions = {}
) {
  const { enabled = true } = options
  return useQuery<WalletBalance | null>({
    queryKey: address
      ? walletBalanceQueryKey(address)
      : ["walletBalance", "__none__"],
    queryFn: async () => {
      if (!address) return null
      const res = await fetch(
        `https://toncenter.com/api/v2/getAddressBalance?address=${encodeURIComponent(address)}`
      )
      if (!res.ok) return null
      const data = await res.json()
      if (!data.ok || data.result == null) return null
      const nano = parseInt(data.result, 10)
      if (!Number.isFinite(nano)) return null
      return { nano, ton: nano / 1e9 }
    },
    enabled: Boolean(address) && enabled,
    staleTime: 1000 * 120,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })
}
